import { observer } from "mobx-react";
import { Dialog } from "../components-v2/Dialog";
import {
  createMeetingRequest,
  createNewMeetingDialogModel,
  DocumentSelection,
  editMeeting,
  isValidNewMeeting,
  MeetingForm,
  MeetingOption,
  MeetingType,
  newMeetingCaptions,
  NewMeetingDialogModel,
} from "./meetingsDomain";
import { Box, VBox } from "../components-v2/Box";
import { FC, useMemo, useState } from "react";
import { MRadioButtonGroup } from "../components-v2/RadioButton";
import { DefaultLanguageSettingsTip, LocalDateTimePicker } from "../dbot/common/commonComponents";
import { isMobileView } from "../utils/screen";
import { TMessage } from "../i18n/tt";
import { MCheckboxGroup } from "../components-v2/Checkbox";
import { Alert } from "../components-v2/Alert";
import { Markdown } from "../text/Markdown";
import { MTextInput } from "../components-v2/TextInput";
import { DocumentLanguage } from "../common/common";
import {
  useLanguage,
  useMessageStore,
  useRootStore,
  useTranslation,
} from "../stores/RootStoreContext";
import { CreateMeetingRequest } from "../dbot/project/projectDomain";

import { EventDispatcher } from "../events/eventsDomain";
import {
  LocalDateFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "../common/commonForms";
import { LinkButton } from "../components-v2/Button";
import { InfoButton } from "../components-v2/InfoButton";
import { LiveGeneralMeetingAnnouncementDialog } from "./generalMeeting/LiveGeneralMeetingAnnouncementDialog";
import { formatDateAndTime } from "../i18n/dateFormat";
import { findLiveGeneralMeetingPricingInterval } from "./generalMeeting/electronicGeneralMeetingDomain";

const NewMeetingDialogInitializer = observer(function NewMeetingDialogInitializer({
  meetingType,
  organisationNumber,
  projectName,
  onClose,
  projectId,
}: {
  meetingType: MeetingType;
  organisationNumber: string;
  projectName: TMessage;
  onClose: () => void;
  projectId?: string;
}) {
  const { t } = useTranslation();
  const { apiService, userStore, dispatch, userPrefs } = useRootStore();
  const dismissedDefaultDocumentLanguageTip =
    userPrefs.dismissedDefaultDocumentLanguageTip ?? false;
  const history = useRootStore().history;
  const model: NewMeetingDialogModel = useMemo(
    () =>
      createNewMeetingDialogModel(
        organisationNumber,
        t(projectName),
        meetingType,
        userStore.companyInfo,
        userStore.companyInfo?.documentSettings,
      ),
    [organisationNumber, t, projectName, userStore.companyInfo, meetingType],
  );

  const onSubmit = (request: CreateMeetingRequest) => {
    return apiService
      .newMeeting(request)
      .then((res) => res.projectId)
      .then(editMeeting(organisationNumber, history))
      .then(onClose);
  };
  return (
    <NewMeetingDialog
      orgNo={organisationNumber}
      meetingType={meetingType}
      onClose={onClose}
      model={model}
      onSubmit={onSubmit}
      projectId={projectId}
      hasSavedDefaultDocumentLanguage={
        userStore.companyInfo?.documentSettings?.defaultDocumentLanguages !== undefined
      }
      dispatch={dispatch}
      dismissedDefaultDocumentLanguageTip={dismissedDefaultDocumentLanguageTip}
    />
  );
});

export type NewMeetingDialogProps = {
  orgNo: string;
  meetingType: MeetingType;
  onClose: () => void;
  model: NewMeetingDialogModel;
  onSubmit: (request: CreateMeetingRequest) => void;
  projectId?: string;
  hasSavedDefaultDocumentLanguage: boolean;
  dismissedDefaultDocumentLanguageTip: boolean;
  dispatch: EventDispatcher;
};

export const NewMeetingDialog = observer(function NewMeetingDialog({
  orgNo,
  meetingType,
  onClose,
  onSubmit,
  model,
  projectId,
  hasSavedDefaultDocumentLanguage,
  dismissedDefaultDocumentLanguageTip,
  dispatch,
}: NewMeetingDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const messageStore = useMessageStore();
  const locale = useLanguage();
  const validateAndCreateNewMeeting = () => {
    if (isValidNewMeeting(model)) {
      setIsLoading(true);
      try {
        onSubmit(createMeetingRequest(orgNo, meetingType, model, projectId));
      } catch (error) {
        messageStore.showGeneralError();
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Dialog
      fullscreen={isMobileView()}
      maxWidth="sm"
      title={t(newMeetingCaptions[meetingType])}
      onCancel={onClose}
      onSubmit={validateAndCreateNewMeeting}
      submitLabel={t("continue")}
      isSubmitting={isLoading}
      isSubmitDisabled={model.meetingOptions.isNoneSelected}
    >
      <VBox gap="lg">
        <MeetingProjectNameView model={model.projectName} />
        {meetingType === MeetingType.GENERAL_MEETING && (
          <MRadioButtonGroup model={model.ordinaryGeneralMeeting} horizontal />
        )}
        <MeetingFormView model={model.meetingForm} />
        <MeetingDateView
          meetingStartDate={model.meetingStartDate}
          meetingStartTime={model.meetingStartTime}
          meetingEndTime={model.meetingEndTime}
        />
        {meetingType === MeetingType.GENERAL_MEETING ? (
          <VBox>
            {t("new-meeting.meeting-options")}
            <MCheckboxGroup
              model={model.meetingOptions}
              horizontal
              onChange={(_, option, checked) => model.onMeetingOptionChange(option, checked)}
            />
            {model.displayElectronicGMDialog.value && (
              <LiveGeneralMeetingAnnouncementDialog
                shareholderPricingInterval={findLiveGeneralMeetingPricingInterval(
                  model.companyInfo,
                )}
                onClose={() => {
                  model.displayElectronicGMDialog.setValue(false);
                  model.onMeetingOptionChange(MeetingOption.MEETING, false);
                }}
                submitLabel={{ key: "release-announcement.general-meeting.order" }}
                onSubmit={() => {
                  dispatch([
                    {
                      type: "order-live-general-meeting",
                      orgNo,
                      generalMeetingDate: formatDateAndTime(
                        locale,
                        model.meetingStartDate.inputValue,
                        model.meetingStartTime.inputValue,
                      ),
                      onSuccess: () => model.displayElectronicGMDialog.setValue(false),
                      onFailure: () => model.displayElectronicGMDialog.setValue(false),
                    },
                  ]);
                }}
                closeLabel={{ key: "cancel" }}
              />
            )}
          </VBox>
        ) : (
          <DocumentSelectionView model={model.documentSelection} />
        )}
        <DocumentLanguageView model={model.meetingDocumentLanguage} />
        <DefaultLanguageSettingsTip
          hasSavedDefaultDocumentLanguage={hasSavedDefaultDocumentLanguage}
          orgNo={orgNo}
          dismissedDefaultDocumentLanguageTip={dismissedDefaultDocumentLanguageTip}
          dispatch={dispatch}
        />
        {projectId === undefined && meetingType === MeetingType.GENERAL_MEETING && (
          <Alert
            type={"info"}
            message={
              <div>
                <Markdown value={t("new-meeting.notice.info")} />
                <LinkButton
                  label={t("release-announcement.general-meeting.schedule-more-details")}
                  onClick={() => dispatch([{ type: "showGeneralMeetingAnnouncementDialog" }])}
                />
              </div>
            }
          />
        )}
        {projectId !== undefined && (
          <Alert
            type={"info"}
            message={<Markdown value={t("dbot.duplicate.noAttachmentsIncluded")} />}
          />
        )}
      </VBox>
    </Dialog>
  );
});

const MeetingProjectNameView: FC<{ model: StringFieldModel }> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <Box>
        {t("dbot.new-project-name.title")}
        <InfoButton infoText={t("dbot.new-project-name.info")} />
      </Box>
      <MTextInput model={model} />
    </VBox>
  );
});

const MeetingFormView: FC<{ model: RadioFieldModel<MeetingForm> }> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      {t("documents.meetingForm.header")}
      <MRadioButtonGroup model={model} horizontal={!isMobileView()} />
    </VBox>
  );
});

const MeetingDateView: FC<{
  meetingStartDate: LocalDateFieldModel;
  meetingStartTime: TimeFieldModel;
  meetingEndTime: TimeFieldModel;
}> = observer(({ meetingStartDate: meetingStartDate, meetingStartTime, meetingEndTime }) => (
  <LocalDateTimePicker
    dateLabel={{ key: "date" }}
    dateField={meetingStartDate}
    timeLabel={{ key: "documents.boardMeeting.dateBoardMeetingStartTimeLabel" }}
    timeField={meetingStartTime}
    endTimeLabel={{ key: "documents.boardMeeting.dateBoardMeetingEndTimeLabel" }}
    endTimeField={meetingEndTime}
  />
));

const DocumentSelectionView: FC<{ model: RadioFieldModel<DocumentSelection> }> = observer(
  ({ model }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        {t("new-meeting.documents-selection")}
        <MRadioButtonGroup model={model} horizontal />
      </VBox>
    );
  },
);

const DocumentLanguageView: FC<{ model: RadioFieldModel<DocumentLanguage> }> = observer(
  ({ model }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        {t("new-meeting.documents-language")}
        <MRadioButtonGroup model={model} horizontal={!isMobileView()} />
      </VBox>
    );
  },
);

export default NewMeetingDialogInitializer;
